import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import paymentService from '../services/paymentService';
import localStorageService from '../services/localStorageService';
import { CHECKOUT, DASHBOARD, LOGIN, SUBSCRIPTION } from '../constants/routes';
import AuthService from '../services/authService';
import { AuthContext } from '../store/ContextStore';
import { normalizeUserFromSubscription } from '../services/trialService';

type UiState = 'pending' | 'success' | 'failed' | 'canceled' | 'timeout' | 'error' | 'missing_session';

function getQueryParam(search: string, key: string): string | null {
  const params = new URLSearchParams(search);
  return params.get(key);
}

function normalizePlans(payload: any): any[] {
  const candidates = [payload?.plans, payload?.plan_list, payload?.planList, payload?.items, payload?.line_items];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  const sportCodes: any[] = Array.isArray(payload?.sport_codes) ? payload.sport_codes : [];
  const subIds: any[] = Array.isArray(payload?.team_subscription_ids) ? payload.team_subscription_ids : [];
  if (sportCodes.length > 0 || subIds.length > 0) {
    const n = Math.max(sportCodes.length, subIds.length);
    return Array.from({ length: n }).map((_, i) => ({
      sport_code: sportCodes[i] ?? '-',
      team_subscription_id: subIds[i] ?? '-',
    }));
  }
  return [];
}

function normalizeAddons(payload: any): any[] {
  const candidates = [payload?.addons, payload?.addon_list, payload?.addonList];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

function normalizeSummary(payload: any): Record<string, any> {
  return (
    payload?.pricing_summary ||
    payload?.pricingSummary ||
    payload?.summary ||
    {
      amount: payload?.amount,
      currency: payload?.currency,
      tax: payload?.tax,
      total: payload?.total,
    }
  );
}

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, setTeam, setSelectedSport } = useContext(AuthContext);

  const sessionId = useMemo(() => getQueryParam(location.search, 'session_id'), [location.search]);

  const [uiState, setUiState] = useState<UiState>('pending');
  const [message, setMessage] = useState<string>('');
  const [data, setData] = useState<any>(null);
  const refreshedRef = useRef(false);

  const token = useMemo(() => {
    const userInfo = localStorageService.getUserInfo();
    return userInfo?.access_token || '';
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setUiState('missing_session');
      setMessage('Missing session id in URL.');
      return;
    }
    if (!token) {
      setUiState('error');
      setMessage('Missing access token. Please log in again to view payment status.');
      return;
    }

    const terminalKey = `payment_terminal_${sessionId}`;
    const refreshedKey = `payment_refreshed_${sessionId}`;
    const payloadKey = `payment_payload_${sessionId}`;
    const existingTerminal = sessionStorage.getItem(terminalKey) as UiState | null;
    if (existingTerminal && existingTerminal !== 'pending') {
      setUiState(existingTerminal);
      try {
        const raw = sessionStorage.getItem(payloadKey);
        if (raw) setData(JSON.parse(raw));
      } catch {
        // ignore bad cached payload
      }
      setMessage(
        existingTerminal === 'success'
          ? 'Your access is updated.'
        : existingTerminal === 'failed'
          ? 'We could not complete the payment.'
            : existingTerminal === 'canceled'
              ? 'You canceled the payment.'
              : existingTerminal === 'timeout'
                ? 'Timed out waiting for payment confirmation. Please refresh in a moment.'
                : existingTerminal === 'missing_session'
                  ? 'Missing session id in URL.'
                  : existingTerminal === 'error'
                    ? 'Server error: unable to confirm payment status. Please try again.'
                    : ''
      );
      return;
    }

    let cancelled = false;
    let done = false;
    let inFlight = false;
    const pollStartedAtMs = Date.now();
    const timeoutMs = 60_000;
    const pollEveryMs = 2_000;
    let timeoutId: number | undefined;

    const stop = () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      timeoutId = undefined;
    };

    const poll = async (): Promise<boolean> => {
      if (cancelled || done || inFlight) return Boolean(done);
      inFlight = true;
      try {
        const res = await paymentService.getPaymentStatus(sessionId);
        const payload = res?.data ?? res;

        if (cancelled) return true;

        setData(payload);

        const rawStatus = String(payload?.status || payload?.payment_status || payload?.paymentStatus || '').toLowerCase();

        if (rawStatus === 'pending') {
          setUiState('pending');
          setMessage('Payment in progress…');
          return false;
        }

        if (rawStatus === 'failed') {
          setUiState('failed');
          setMessage('We could not complete the payment.');
          try { sessionStorage.setItem(payloadKey, JSON.stringify(payload)); } catch {}
          sessionStorage.setItem(terminalKey, 'failed');
          done = true;
          stop();
          return true;
        }

        if (rawStatus === 'success') {
          // Wait for refresh to complete before showing the success modal.
          setUiState('pending');
          setMessage('Payment confirmed. Finalizing your access…');

          if (!sessionStorage.getItem(refreshedKey) && !refreshedRef.current) {
            sessionStorage.setItem(refreshedKey, 'true');
            refreshedRef.current = true;
            try {
              const userInfo = localStorageService.getUserInfo();
              const refreshToken: string = userInfo?.refresh_token || userInfo?.refreshToken || '';
              if (refreshToken) {
                const authService = new AuthService();
                const refreshRes: any = await authService.RefreshAuth(refreshToken);
                const refreshPayload = refreshRes?.data ?? refreshRes;
                if (refreshRes?.status >= 200 && refreshRes?.status < 300) {
                  const accessToken: string = refreshPayload?.access_token || userInfo?.access_token || '';
                  const nextRefreshToken: string = refreshPayload?.refresh_token || refreshToken;

                  const nextUser = normalizeUserFromSubscription({
                    ...(userInfo || {}),
                    access_token: accessToken,
                    token: accessToken,
                    refresh_token: nextRefreshToken,
                    subscription: refreshPayload?.subscription,
                  });

                  localStorageService.addUserInfo(nextUser);
                  setUser(nextUser);
                  setTeam(nextUser?.team || {});

                const sportCodes: string[] = Array.isArray(nextUser?.sports)
                  ? nextUser.sports.map((s: any) => s?.code).filter(Boolean)
                  : [];
                if (sportCodes.length > 0) {
                  const currentSelected = localStorageService.getSelectedSport('');
                  const nextSelected = sportCodes.includes('MFB')
                    ? 'MFB'
                    : (sportCodes.includes(currentSelected) ? currentSelected : sportCodes[0]);
                  localStorageService.setSelectedSport(nextSelected);
                  setSelectedSport(nextSelected);
                }
                }
              }
            } catch {
              // ignore refresh errors; still show success modal
            }
          }

          setUiState('success');
          setMessage('Your access is updated.');
          try { sessionStorage.setItem(payloadKey, JSON.stringify(payload)); } catch {}
          sessionStorage.setItem(terminalKey, 'success');
          done = true;
          stop();
          return true;
        }

        // Unknown status: keep polling but show processing.
        setUiState('pending');
        setMessage('Payment in progress…');
        return false;
      } catch (err: any) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to fetch payment status.';
        setUiState('error');
        setMessage(String(msg));
        sessionStorage.setItem(terminalKey, 'error');
        done = true;
        stop();
        return true;
      } finally {
        inFlight = false;
      }
    };

    const scheduleNext = () => {
      if (cancelled || done) return;
      const elapsed = Date.now() - pollStartedAtMs;
      if (elapsed > timeoutMs) {
        stop();
        if (!cancelled) {
          setUiState('timeout');
          setMessage('Payment is still processing. Please wait a moment and refresh.');
          sessionStorage.setItem(terminalKey, 'timeout');
          done = true;
        }
        return;
      }
      timeoutId = window.setTimeout(async () => {
        const terminal = await poll();
        if (!terminal) scheduleNext();
      }, pollEveryMs);
    };

    // First poll immediately, then schedule next only if still pending.
    poll().then((terminal) => {
      if (!terminal) scheduleNext();
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, [sessionId, token]);

  const plans = useMemo(() => normalizePlans(data), [data]);
  const addons = useMemo(() => normalizeAddons(data), [data]);

  const planDisplay = useMemo(() => {
    const sportCodes: string[] = Array.isArray(data?.sport_codes) ? data.sport_codes : [];
    const teamSubs: string[] = Array.isArray(data?.team_subscription_ids) ? data.team_subscription_ids : [];
    const teamSubToSport = new Map<string, string>();
    sportCodes.forEach((code, idx) => {
      const teamSubId = teamSubs[idx];
      if (teamSubId) teamSubToSport.set(String(teamSubId), String(code));
    });

    const groupedAddons = new Map<string, any[]>();
    addons.forEach((a: any) => {
      const teamSubId = a?.team_subscription_id || a?.teamSubscriptionId;
      const sport = teamSubId ? teamSubToSport.get(String(teamSubId)) : undefined;
      const sportCode = sport || String(a?.sport_code || a?.sportCode || 'UNKNOWN');
      const list = groupedAddons.get(sportCode) || [];
      list.push(a);
      groupedAddons.set(sportCode, list);
    });

    const sports = sportCodes.length > 0 ? sportCodes : Array.from(groupedAddons.keys());
    return sports.map((sportCode) => {
      const list = groupedAddons.get(String(sportCode)) || [];
      const addonNames = Array.from(
        new Set(list.map((a: any) => String(a?.addon_name || a?.addonName || 'Addon')))
      );
      const seasons = Array.from(
        new Set(
          list
            .flatMap((a: any) => (Array.isArray(a?.selected_seasons) ? a.selected_seasons : []))
            .map((s: any) => String(s))
        )
      );
      return { sportCode: String(sportCode), addonNames, seasons, rawAddons: list };
    });
  }, [data, addons]);

  const title =
    uiState === 'success'
      ? 'Payment Successful'
      : uiState === 'failed'
        ? 'Payment Failed'
        : uiState === 'canceled'
          ? 'Payment Canceled'
          : uiState === 'timeout'
            ? 'Payment Pending'
            : uiState === 'missing_session'
              ? 'Missing Session'
              : uiState === 'error'
                ? 'Unable to Load Status'
                : 'Checking Payment Status';

  const badgeClass =
    uiState === 'success'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : uiState === 'failed' || uiState === 'canceled' || uiState === 'error'
        ? 'bg-red-50 text-red-700 border-red-200'
        : 'bg-slate-50 text-slate-700 border-slate-200';

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#E8EBF3] via-[#F0F3FA] to-[#F8FAFC]">
      <div className="w-full pt-12 pb-16 flex flex-col items-center justify-center">
        <div className="w-full max-w-3xl px-6">
          <div className="mb-6 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">{title}</h2>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${badgeClass}`}>
              <span className="text-sm font-semibold">{message || ' '}</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            {(planDisplay.length > 0 || plans.length > 0 || addons.length > 0) && uiState !== 'pending' && (
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">Plan details</h3>

                <div className="space-y-4">
                  {planDisplay.map((row) => (
                    <div key={row.sportCode} className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
                      <div className="text-sm text-slate-700">
                        <span className="font-semibold text-slate-900">Sport:</span> {row.sportCode}
                      </div>

                      {row.addonNames.length > 0 && (
                        <div className="mt-2 text-sm text-slate-700">
                          <span className="font-semibold text-slate-900">Addons:</span>{' '}
                          {row.addonNames.join(', ')}
                        </div>
                      )}

                      {row.seasons.length > 0 && (
                        <div className="mt-2 text-sm text-slate-700">
                          <span className="font-semibold text-slate-900">Selected Season:</span>{' '}
                          {row.seasons.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-4">
                  <span className="text-slate-700 font-semibold">Amount:</span>
                  <span className="text-slate-900 font-bold">
                    {data?.amount ?? '-'} {String(data?.currency || '').toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            {uiState === 'pending' && (
              <div className="text-center text-slate-700 font-semibold py-10">
                Payment in progress…
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              {(uiState === 'failed' || uiState === 'canceled' || uiState === 'timeout' || uiState === 'error') && (
                <button
                  onClick={() => navigate(SUBSCRIPTION)}
                  className="px-5 py-2.5 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors"
                >
                  Back to Subscription
                </button>
              )}
              {(uiState === 'failed' || uiState === 'canceled' || uiState === 'timeout') && (
                <button
                  onClick={() => navigate(SUBSCRIPTION)}
                  className="px-5 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-800 font-semibold hover:bg-slate-50 transition-colors"
                >
                  Change Plan
                </button>
              )}
              {uiState === 'success' && (
                <button
                  onClick={() => navigate(DASHBOARD)}
                  className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors"
                >
                  Go to Dashboard
                </button>
              )}
              {(uiState === 'missing_session' || (uiState === 'error' && !token)) && (
                <button
                  onClick={() => navigate(LOGIN)}
                  className="px-5 py-2.5 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors"
                >
                  Log In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
